import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Fieldset} from '../_fieldset';
import {
  SelectFieldShell,
  SelectMenuPanel,
  SelectSearchInput,
  StaticControl,
  flattenGroupedOptions,
  mergeComponents,
  useSelectState,
  type SelectChangeMeta,
  type SelectComponents,
  type SelectFilterOption,
  type SelectFormatGroupLabel,
  type SelectFormatOptionLabel,
  type SelectGroupedOption,
  type SelectIsOptionDisabled,
  type SelectIsOptionSelected,
  type SelectValue,
} from '../_select-internals';
import type {SelectOption} from '../../lib/selector-option';
import {cn} from '../../lib/cn';
import type {ReactNode} from 'react';
import {useSetCorrectOptionIfThereIsOnlyValue} from './useSetCorrectOptionIfThereIsOnlyValue';

type BaseSelectProps<T, V extends SelectValue, L extends ReactNode> = {
  options?: SelectGroupedOption<T, V, L>[];
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  label?: string;
  topLabel?: string;
  placeholder?: string;
  getValueLabel?: (option: SelectOption<T, V, L>) => string;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  menuLoading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  error?: string;
  invalid?: boolean;
  hideErrorMessage?: boolean;
  className?: string;
  menuClassName?: string;
  dropdownClassName?: string;
  drawerClassName?: string;
  fieldsetClassName?: string;
  name?: string;
  width?: number | string;
  noOptionsMessage?: () => string | undefined;
  filterOption?: SelectFilterOption<T, V, L>;
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>;
  isOptionSelected?: SelectIsOptionSelected<T, V, L>;
  formatOptionLabel?: SelectFormatOptionLabel<T, V, L>;
  helperText?: string;
  isCreatable?: boolean;
  onCreateOption?: (input: string) => SelectOption<T, V, L>;
  formatCreateLabel?: (input: string) => React.ReactNode;
  isValidNewOption?: (
    input: string,
    selectedOptions: SelectOption<T, V, L>[],
    options: SelectOption<T, V, L>[],
  ) => boolean;
  menuIsOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  autoFocus?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  hideIndicator?: boolean;
  openMenuOnFocus?: boolean;
  components?: SelectComponents<T, V, L>;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  searchable?: boolean;
  searchPosition?: 'trigger' | 'dropdown';
  menuHeader?: React.ReactNode;
  onMenuScrollToBottom?: (event: React.UIEvent<HTMLDivElement>) => void;
  leftIcon?: React.ReactNode;
  formatGroupLabel?: SelectFormatGroupLabel<T, V, L>;
  onReset?: () => void;
};

type SingleSelectProps<T, V extends SelectValue, L extends ReactNode> = BaseSelectProps<
  T,
  V,
  L
> & {
  isMulti?: false;
  value?: SelectOption<T, V, L> | null;
  defaultValue?: SelectOption<T, V, L> | null;
  onChange?: (option: SelectOption<T, V, L> | null, meta?: SelectChangeMeta) => void;
  clearable?: boolean;
};

type MultiSelectModeProps<
  T,
  V extends SelectValue,
  L extends ReactNode,
> = BaseSelectProps<T, V, L> & {
  isMulti: true;
  value?: SelectOption<T, V, L>[] | null;
  defaultValue?: SelectOption<T, V, L>[] | null;
  onChange?: (value: SelectOption<T, V, L>[], meta?: SelectChangeMeta) => void;
  closeMenuOnSelect?: boolean;
};

type SelectProps<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
> = SingleSelectProps<T, V, L> | MultiSelectModeProps<T, V, L>;

const SEARCHABLE_OPTIONS_THRESHOLD = 15;

function SelectInternal<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(props: SelectProps<T, V, L>, ref: React.Ref<HTMLDivElement>) {
  const {
    options = [],
    onBlur,
    onFocus,
    label,
    topLabel,
    placeholder,
    getValueLabel,
    disabled,
    readOnly,
    loading,
    menuLoading,
    optional,
    tooltip,
    error,
    invalid,
    hideErrorMessage,
    className,
    menuClassName,
    dropdownClassName,
    drawerClassName,
    fieldsetClassName,
    name,
    width,
    noOptionsMessage,
    filterOption,
    isOptionDisabled,
    isOptionSelected,
    formatOptionLabel,
    helperText,
    isCreatable = false,
    onCreateOption,
    formatCreateLabel,
    isValidNewOption,
    menuIsOpen,
    onMenuOpenChange,
    autoFocus,
    onKeyDown,
    hideIndicator,
    openMenuOnFocus,
    components: userComponents,
    inputValue,
    onInputChange,
    searchable: searchableProp,
    searchPosition = 'trigger',
    menuHeader,
    onMenuScrollToBottom,
    leftIcon,
    formatGroupLabel,
    onReset,
  } = props;
  const isMulti = props.isMulti === true;
  const flatOptions = React.useMemo(() => flattenGroupedOptions(options), [options]);
  const searchable = searchableProp ?? flatOptions.length > SEARCHABLE_OPTIONS_THRESHOLD;
  const isSearchInDropdown = searchable && searchPosition === 'dropdown';
  const clearable = isMulti || props.clearable;
  const closeMenuOnSelect = isMulti ? (props.closeMenuOnSelect ?? false) : undefined;

  const {t} = useTranslation();

  const isControlled = props.value !== undefined;
  const [internalValue, setInternalValue] = React.useState<
    SelectOption<T, V, L> | SelectOption<T, V, L>[] | null
  >(() => {
    if (isMulti) return (props.defaultValue as SelectOption<T, V, L>[] | null) ?? [];
    return (props.defaultValue as SelectOption<T, V, L> | null) ?? null;
  });

  const currentValue = isControlled ? props.value : internalValue;

  useSetCorrectOptionIfThereIsOnlyValue<T, V, L>({
    enabled: !isMulti,
    value: !isMulti
      ? (props.value as SelectOption<T, V, L> | null | undefined)
      : undefined,
    options,
    onChange: !isMulti
      ? (props.onChange as SingleSelectProps<T, V, L>['onChange'])
      : undefined,
  });

  const selectedOptions = React.useMemo(() => {
    if (isMulti) return (currentValue as SelectOption<T, V, L>[] | null) ?? [];
    return currentValue ? [currentValue as SelectOption<T, V, L>] : [];
  }, [isMulti, currentValue]);

  const onSelectionChange = React.useCallback(
    (next: SelectOption<T, V, L>[], meta: SelectChangeMeta) => {
      if (isMulti) {
        if (!isControlled) setInternalValue(next);
        (props.onChange as MultiSelectModeProps<T, V, L>['onChange'])?.(next, meta);
      } else {
        const nextValue = next[0] ?? null;
        if (!isControlled) setInternalValue(nextValue);
        (props.onChange as SingleSelectProps<T, V, L>['onChange'])?.(nextValue, meta);
      }
    },
    [isMulti, isControlled, props.onChange],
  );

  const state = useSelectState<T, V, L>({
    isMulti,
    options: flatOptions,
    selectedOptions,
    onSelectionChange,
    filterOption,
    isOptionDisabled,
    isOptionSelected,
    isCreatable,
    onCreateOption,
    isValidNewOption,
    getValueLabel,
    menuIsOpen,
    onMenuOpenChange,
    name,
    error,
    hideErrorMessage,
    disabled,
    loading,
    readOnly,
    closeMenuOnSelect,
    openMenuOnFocus,
    onKeyDown,
    onFocus,
    onBlur,
    inputValue,
    onInputChange,
    isSearchInDropdown,
    searchable,
  });

  const components = React.useMemo(() => {
    const merged = mergeComponents<T, V, L>(userComponents);
    if ((isSearchInDropdown || (!searchable && !isMulti)) && !userComponents?.Control) {
      return {...merged, Control: StaticControl as typeof merged.Control};
    }
    return merged;
  }, [userComponents, isSearchInDropdown, searchable, isMulti]);

  React.useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    ref as React.Ref<HTMLDivElement | null>,
    () => state.containerRef.current,
  );

  const hasLabel = Boolean(label);
  const resolvedTitle = label ?? placeholder;
  const accessibleLabel =
    !hasLabel && typeof placeholder === 'string' ? placeholder : undefined;
  const hasInvalidState = state.hasInvalidState || Boolean(invalid);
  const hasLeadingIcon = Boolean(leftIcon);
  const hiddenValue = isMulti
    ? selectedOptions.map(item => String(item.value)).join(',')
    : selectedOptions[0]
      ? String(selectedOptions[0].value)
      : '';

  const handleClear = (event: React.MouseEvent) => {
    state.clearSelection(event);
    onReset?.();
  };

  const handleFieldClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest('[data-select-menu]')) return;
    state.handleContainerClick();
  };

  const {Control, MenuList, CreateOption} = components;

  return (
    <SelectFieldShell
      containerRef={state.containerRef}
      className={className}
      width={width}
      disabled={disabled}
      loading={loading}
      topLabel={topLabel}
      triggerId={state.ids.triggerId}
      errorId={state.ids.errorId}
      errorMessage={state.errorMessage}
      hideErrorMessage={hideErrorMessage}
      optional={optional}
      helperText={helperText}
      name={name}
      hiddenValue={hiddenValue}
      onBlur={state.handleBlur}
      onContentClick={handleFieldClick}
    >
      <Control
        triggerId={state.ids.triggerId}
        listboxId={state.ids.listboxId}
        labelId={state.ids.labelId}
        valueId={state.ids.valueId}
        describedErrorId={state.ids.describedErrorId}
        accessibleLabel={accessibleLabel}
        getOptionId={state.ids.getOptionId}
        inputRef={state.inputRef}
        inputValue={state.inputValue}
        placeholder={placeholder}
        showPlaceholderWhenIdle={!hasLabel}
        isMulti={isMulti}
        isOpen={state.isOpen}
        isFocused={state.isFocused}
        isBlocked={state.isBlocked}
        hasValue={state.hasValue}
        isEmpty={state.isEmpty}
        invalid={hasInvalidState}
        loading={loading}
        disabled={disabled}
        readOnly={readOnly}
        highlightedIndex={state.highlightedIndex}
        selectedOptions={selectedOptions}
        valueLabel={state.valueLabel}
        onInputChange={state.handleInputChange}
        onInputFocus={state.handleInputFocus}
        onInputKeyDown={state.handleInputKeyDown}
        onTriggerFocus={state.handleTriggerFocus}
        onContainerClick={state.handleContainerClick}
        onClear={handleClear}
        onRemoveOption={state.removeOption}
        clearable={clearable}
        clearLabel={isMulti ? t('clear_all') : t('clear')}
        hideIndicator={hideIndicator}
        autoFocus={autoFocus}
        leftIcon={leftIcon}
        searchable={searchable}
        components={components}
      />
      <Fieldset
        isFocused={state.isFocused || state.isOpen}
        invalid={hasInvalidState}
        isEmpty={state.isEmpty}
        isActivated={!state.isEmpty || state.isFocused || state.isOpen}
        disabled={disabled}
        loading={loading}
        readOnly={readOnly}
        htmlFor={state.ids.triggerId}
        labelId={state.ids.labelId}
        legend={label}
        label={label}
        tooltip={tooltip}
        pointerCursor={!searchable || isSearchInDropdown}
        onClick={state.handleContainerClick}
        labelClassName={cn(!hideIndicator && 'pr-10', hasLeadingIcon && 'pl-[28px]')}
        className={fieldsetClassName}
      />
      <SelectMenuPanel
        isOpen={state.isOpen}
        isMobile={state.isMobile}
        onClose={() => {
          state.closeMenu();
          state.setIsFocused(false);
        }}
        title={resolvedTitle}
        className={dropdownClassName}
        drawerClassName={drawerClassName}
        searchVisible={state.inMenuSearchVisible}
      >
        {state.inMenuSearchVisible && (
          <SelectSearchInput
            inputRef={state.mobileSearchInputRef}
            value={state.inputValue}
            placeholder={t('search_placeholder')}
            listboxId={state.ids.listboxId}
            activeOptionId={
              state.highlightedIndex >= 0
                ? state.ids.getOptionId(state.highlightedIndex)
                : undefined
            }
            onChange={state.handleInputChange}
            onKeyDown={state.handleInputKeyDown}
          />
        )}
        {menuHeader}
        <MenuList
          id={state.ids.listboxId}
          labelledBy={state.ids.labelId}
          describedBy={state.ids.describedErrorId}
          getOptionId={state.ids.getOptionId}
          options={state.filteredOptions}
          selectedOptions={selectedOptions}
          isMulti={isMulti}
          highlightedIndex={state.highlightedIndex}
          onOptionHover={state.setHighlightedIndex}
          onOptionClick={state.selectOption}
          disabled={state.isBlocked}
          menuLoading={menuLoading}
          isOptionDisabled={isOptionDisabled}
          listRef={state.listRef}
          registerOptionRef={state.registerOptionRef}
          menuClassName={menuClassName}
          noOptionsMessage={noOptionsMessage}
          components={components}
          inputValue={state.inputValue}
          formatOptionLabel={formatOptionLabel}
          isOptionSelected={isOptionSelected}
          onMenuScrollToBottom={onMenuScrollToBottom}
          groupedOptions={options}
          formatGroupLabel={formatGroupLabel}
        />
        {state.canCreateNewOption && (
          <CreateOption
            inputValue={state.trimmedInput}
            onCreate={state.createOption}
            formatCreateLabel={formatCreateLabel}
          />
        )}
      </SelectMenuPanel>
    </SelectFieldShell>
  );
}

type SelectComponent = <
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(
  props: SelectProps<T, V, L> & {ref?: React.Ref<HTMLDivElement>},
) => React.ReactElement;

const Select = React.forwardRef(SelectInternal) as unknown as SelectComponent;

export {Select};
export type {
  SelectProps,
  SingleSelectProps,
  MultiSelectModeProps as SelectMultiProps,
  BaseSelectProps,
};
